import React from 'react';
import { useToggleObject } from '../hooks';
import { useCreateUserConversationMutation, useUserConversationsQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { useFeatureFlag } from '@uplift/hooks';

type MessagingTab = 'messages' | 'todos';
export type MessagingContextProps = {
  tab: MessagingTab;
  setTab: (tab: MessagingTab) => void;
  modalOpened: boolean;
  openModal: (conversationId?: string, messageId?: string) => void;
  closeModal: () => void;
  unreadMessagesCount: number;
  openToDo: (toDoId: string) => void;
  autoSelectToDoFilter: boolean;
  setAutoSelectToDoFilter: (value: boolean) => void;
  selectedToDoId?: string;
  setSelectedToDoId: (id?: string) => void;
  selectedConversationId?: string;
  setSelectedConversationId: (id?: string) => void;
  focusedMessageId?: string;
  setFocusedMessageId: (messageId?: string) => void;
  createChat: (participantId: string) => Promise<string>;
};

const defaultValue: MessagingContextProps = {
  tab: 'messages',
  setTab: () => undefined,
  modalOpened: false,
  openModal: () => undefined,
  closeModal: () => undefined,
  openToDo: () => undefined,
  unreadMessagesCount: 0,
  focusedMessageId: undefined,
  setFocusedMessageId: () => undefined,
  autoSelectToDoFilter: false,
  setAutoSelectToDoFilter: () => undefined,
  selectedToDoId: undefined,
  setSelectedToDoId: () => undefined,
  selectedConversationId: undefined,
  setSelectedConversationId: () => undefined,
  createChat: async () => '',
};
const MessagingContext = React.createContext<MessagingContextProps>(defaultValue);


export const useMessagingContext = () => {
  const context = React.useContext(MessagingContext);

  if (context === defaultValue) {
    console.warn('useMessagingContext must be used within a MessagingProvider');
  }

  return context;
};

export const useSelectedConversation = () => {
  const messaging = useMessagingContext();
  const { data: conversations = [] } = useUserConversationsQuery({ enabled: messaging.modalOpened });
  const selectedConversation = React.useMemo(() => conversations.find(c => c.id === messaging.selectedConversationId), [ conversations, messaging.selectedConversationId ]);

  return selectedConversation;
};


export const MessagingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const me = useCurrentuser();
  const toggle = useToggleObject(false);
  const IN_APP_MESSAGING = useFeatureFlag('IN_APP_MESSAGING');
  const [ tab, setTab ] = React.useState<MessagingTab>('messages');
  const createConversationMutation = useCreateUserConversationMutation();
  const [ autoSelectToDoFilter, setAutoSelectToDoFilter ] = React.useState<boolean>(false);
  const [ selectedToDoId, setSelectedToDoId ] = React.useState<string | undefined>(undefined);
  const [ selectedConversationId, setSelectedConversationId ] = React.useState<string | undefined>(undefined);
  const [ focusedMessageId, setFocusedMessageId ] = React.useState<string | undefined>(undefined);
  const { data: conversations = [] } = useUserConversationsQuery({ enabled: !!me.id && !!IN_APP_MESSAGING });
  const unreadMessagesCount = React.useMemo(() => {
    return conversations.reduce((count, conversation) => {
      return count + (conversation.unreadMessagesCount || 0);
    }, 0);
  }, [ conversations ]);

  return (
    <MessagingContext.Provider value={{
      tab,
      setTab,
      openToDo: (toDoId: string) => {
        setAutoSelectToDoFilter(true);
        setSelectedToDoId(toDoId);
        setTab('todos');
      },
      autoSelectToDoFilter,
      setAutoSelectToDoFilter,
      selectedToDoId,
      setSelectedToDoId,
      selectedConversationId,
      setSelectedConversationId,
      focusedMessageId,
      setFocusedMessageId,
      modalOpened: toggle.value,
      openModal: (conversationId?: string, messageId?: string) => {
        setTab('messages');
        if (conversationId) {
          setSelectedConversationId(conversationId);
        }
        if (messageId) {
          setFocusedMessageId(messageId);
        }
        toggle.open();
      },
      closeModal: toggle.close,
      unreadMessagesCount: unreadMessagesCount,
      createChat: async (participantId) => {
        const response = await createConversationMutation.mutateAsync({ participantId });

        return response.data.conversationId;
      }
    }}
    >
      {children}
    </MessagingContext.Provider>
  );
};
